import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Download, Trash2, ShieldCheck, ShieldAlert, Clock, History, FileStack, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ConfirmTypeDialog from "@/components/ConfirmTypeDialog";

export default function Certificates() {
  const { user } = useAuth();
  const canIssue = ["admin","super_admin"].includes(user?.role) || user?.role === "inspector";
  const canApprove = ["admin","super_admin"].includes(user?.role) || user?.role === "approver";
  const location = useLocation();
  const [certs, setCerts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState({});
  const today = new Date().toISOString().slice(0, 10);
  const inYear = new Date(Date.now() + 365 * 86400e3).toISOString().slice(0, 10);
  const [form, setForm] = useState({ asset_id: "", inspection_id: "", certificate_number: `CERT-${Date.now()}`, issued_date: today, expiry_date: inYear, issued_by: user?.name || "", standard: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [auditFor, setAuditFor] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmBulkDel, setConfirmBulkDel] = useState(false);

  const load = async () => {
    const [c, a] = await Promise.all([api.get("/certificates"), api.get("/assets")]);
    setCerts(c.data); setAssets(a.data); setSelected({});
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (location.state?.prefill) { setForm((f) => ({ ...f, ...location.state.prefill })); setOpen(true); }
  }, [location.state]);

  const submit = async () => {
    if (!form.asset_id || !form.certificate_number || !form.issued_by) return toast.error("Fill required fields");
    setSaving(true);
    try {
      await api.post("/certificates", form);
      toast.success("Certificate issued — sent for approval");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  const download = async (id, num) => {
    try {
      const res = await api.get(`/certificates/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `cert-${num}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download"); }
  };

  const downloadFromTemplate = async (id, num) => {
    try {
      const res = await api.get(`/templates/render/certificate/${id}`, { responseType: "blob" });
      const blob = res.data;
      const ct = blob.type || "";
      const ext = ct.includes("word") ? "docx" : ct.includes("pdf") ? "pdf" : "html";
      const url = URL.createObjectURL(blob);
      if (ext === "docx") {
        const a = document.createElement("a"); a.href = url; a.download = `cert-${num}.${ext}`; a.click();
      } else {
        window.open(url, "_blank", "noopener");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      const raw = e.response?.data;
      const msg = raw instanceof Blob ? await raw.text() : (raw?.detail || "No template configured");
      try { toast.error(JSON.parse(msg).detail || msg); } catch { toast.error(msg); }
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/certificates/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return toast.error("Select at least one certificate");
    try {
      const { data } = await api.post("/certificates/bulk-delete", { ids: selectedIds });
      toast.success(`${data.deleted} certificate(s) deleted`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const approve = async (id) => {
    try { await api.post(`/certificates/${id}/approve`); toast.success("Certificate approved & released to customer"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitReject = async () => {
    if (!rejectReason || rejectReason.length < 3) return toast.error("Please provide a reason (min 3 chars)");
    try {
      await api.post(`/certificates/${rejectFor.id}/reject`, { reason: rejectReason });
      toast.success("Certificate rejected — inspector notified");
      setRejectFor(null); setRejectReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openAudit = async (cert) => {
    setAuditFor(cert); setAuditEvents([]); setAccessLogs([]);
    try {
      const [ev, acc] = await Promise.all([
        api.get(`/certificates/${cert.id}/audit`),
        api.get(`/certificates/${cert.id}/access-log`).catch(() => ({ data: [] })),
      ]);
      setAuditEvents(ev.data); setAccessLogs(acc.data);
    } catch { toast.error("Failed to load audit trail"); }
  };

  const sendToCustomer = async (c) => {
    try {
      const { data } = await api.post(`/certificates/${c.id}/send-to-customer`);
      if (data.mode === "stub") toast.success(`Queued to ${data.email} — configure Resend to deliver for real`);
      else if (data.sent) toast.success(`Certificate emailed to ${data.email}`);
      else toast.error("Email could not be sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
  };

  const pendingCount = certs.filter(c => (c.approval_status || "approved") === "pending").length;
  const approvedCount = certs.filter(c => (c.approval_status || "approved") === "approved").length;
  const rejectedCount = certs.filter(c => c.approval_status === "rejected").length;
  const filtered = tab === "all" ? certs : certs.filter(c => (c.approval_status || "approved") === tab);
  const canBulkAct = canApprove || user?.role === 'admin';
  const selectableIds = filtered.filter(c => (c.approval_status || "approved") !== "approved" || user?.role === 'admin').map(c => c.id);
  const selectedIds = Object.keys(selected).filter(id => selected[id]);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected[id]);

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else { const next = {}; selectableIds.forEach(id => next[id] = true); setSelected(next); }
  };

  const bulkApprove = async () => {
    if (selectedIds.length === 0) return toast.error("Select at least one non-approved certificate");
    try {
      const { data } = await api.post("/certificates/bulk-approve", { ids: selectedIds });
      toast.success(`${data.approved} certificate(s) approved & released`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="certificates-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Compliance</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Certificates</h1>
        </div>
        <div className="flex items-center gap-2">
          {canApprove && selectedIds.length > 0 && (
            <Button data-testid="bulk-approve-btn" onClick={bulkApprove} className="rounded-md bg-[#059669] hover:bg-[#047857]">
              <ShieldCheck className="w-4 h-4 mr-2" /> Approve {selectedIds.length} selected
            </Button>
          )}
          {user?.role === 'admin' && selectedIds.length > 0 && (
            <Button data-testid="bulk-delete-btn" onClick={() => setConfirmBulkDel(true)} className="rounded-md bg-[#DC2626] hover:bg-[#B91C1C]">
              <Trash2 className="w-4 h-4 mr-2" /> Delete {selectedIds.length} selected
            </Button>
          )}
          {canIssue && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-cert-button" className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]"><Plus className="w-4 h-4 mr-2" /> Issue Certificate</Button>
              </DialogTrigger>
              <DialogContent className="rounded-md max-w-lg">
                <DialogHeader><DialogTitle className="font-display">Issue Certificate</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">Asset *</Label>
                    <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
                      <SelectTrigger className="rounded-md mt-1"><SelectValue placeholder="Select asset" /></SelectTrigger>
                      <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">Certificate Number *</Label><Input value={form.certificate_number} onChange={(e) => setForm({ ...form, certificate_number: e.target.value })} className="rounded-md mt-1 font-mono" data-testid="cert-number-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-xs uppercase tracking-wider font-semibold">Issued Date *</Label><Input type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} className="rounded-md mt-1" /></div>
                    <div><Label className="text-xs uppercase tracking-wider font-semibold">Expiry Date *</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="rounded-md mt-1" data-testid="cert-expiry-input" /></div>
                  </div>
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">Issued By *</Label><Input value={form.issued_by} onChange={(e) => setForm({ ...form, issued_by: e.target.value })} className="rounded-md mt-1" /></div>
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">Standard (optional)</Label><Input value={form.standard} onChange={(e) => setForm({ ...form, standard: e.target.value })} placeholder="e.g. ISO 9001, LOLER" className="rounded-md mt-1" /></div>
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-md mt-1" /></div>
                  <div className="text-xs text-gray-500 border-l-2 border-[#F59E0B] pl-3">New certificates are marked <b>Pending Approval</b> until an approver reviews and releases them to the customer.</div>
                </div>
                <DialogFooter>
                  <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button data-testid="save-cert-button" disabled={saving} onClick={submit} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">{saving ? "Saving..." : "Issue"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Approval KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <ApprovalCard data-testid="approval-kpi-pending" onClick={() => setTab("pending")} active={tab === "pending"} icon={Clock} color="bg-[#F59E0B]" label="Pending Approval" value={pendingCount} />
        <ApprovalCard data-testid="approval-kpi-approved" onClick={() => setTab("approved")} active={tab === "approved"} icon={ShieldCheck} color="bg-[#059669]" label="Approved (released)" value={approvedCount} />
        <ApprovalCard data-testid="approval-kpi-rejected" onClick={() => setTab("rejected")} active={tab === "rejected"} icon={ShieldAlert} color="bg-[#DC2626]" label="Rejected" value={rejectedCount} />
      </div>

      <div className="flex items-center gap-1 mb-4">
        {["all", "pending", "approved", "rejected"].map(k => (
          <button key={k} data-testid={`cert-tab-${k}`} onClick={() => { setTab(k); setSelected({}); }}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold border transition-colors ${tab === k ? "bg-[#01ADEF] text-white border-[#01ADEF]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}>
            {k}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              {canBulkAct && <th className="px-4 py-3 w-10"><Checkbox data-testid="select-all-checkbox" checked={allSelected} onCheckedChange={toggleAll} /></th>}
              <Th>Cert #</Th><Th>Asset</Th><Th>Issued</Th><Th>Expires</Th><Th>Validity</Th><Th>Approval</Th><Th>Issued By</Th><Th></Th>
            </tr>
          </thead>
          <tbody data-testid="certs-table">
            {filtered.length === 0 ? <tr><td colSpan={canBulkAct ? 9 : 8} className="text-center py-10 text-gray-500">No certificates in this view.</td></tr>
              : filtered.map((c) => {
                const appr = c.approval_status || "approved";
                const selectable = appr !== "approved" || user?.role === 'admin';
                return (
                  <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`cert-row-${c.id}`}>
                    {canBulkAct && <td className="px-4 py-3">{selectable && <Checkbox data-testid={`select-cert-${c.id}`} checked={!!selected[c.id]} onCheckedChange={(v) => setSelected({ ...selected, [c.id]: !!v })} />}</td>}
                    <Td className="font-mono font-semibold">{c.certificate_number}</Td>
                    <Td><div className="font-semibold">{c.asset_name || '—'}</div><div className="font-mono text-xs text-gray-500">{c.asset_tag}</div></Td>
                    <Td className="font-mono">{c.issued_date}</Td>
                    <Td className="font-mono">{c.expiry_date}</Td>
                    <Td><CertStatus s={c.cert_status} d={c.days_to_expiry} /></Td>
                    <Td>
                      <ApprovalBadge s={appr} />
                      {appr === "rejected" && c.rejection_reason && <div className="text-xs text-[#B91C1C] mt-1 max-w-[220px] truncate" title={c.rejection_reason}>{c.rejection_reason}</div>}
                      {appr === "approved" && c.approver_name && <div className="text-xs text-gray-500 mt-1">by {c.approver_name}</div>}
                    </Td>
                    <Td>{c.issued_by}</Td>
                    <Td className="flex items-center gap-3">
                      {canApprove && appr !== "approved" && (
                        <button onClick={() => approve(c.id)} data-testid={`approve-cert-${c.id}`} title="Approve" className="text-[#059669] hover:text-[#047857]"><ShieldCheck className="w-4 h-4" /></button>
                      )}
                      {canApprove && appr !== "rejected" && (
                        <button onClick={() => { setRejectFor(c); setRejectReason(""); }} data-testid={`reject-cert-${c.id}`} title="Reject" className="text-[#DC2626] hover:text-[#B91C1C]"><ShieldAlert className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => openAudit(c)} data-testid={`audit-cert-${c.id}`} title="Audit trail" className="text-gray-500 hover:text-gray-900"><History className="w-4 h-4" /></button>
                      <button onClick={() => download(c.id, c.certificate_number)} data-testid={`download-cert-${c.id}`} className="text-[#01ADEF] hover:text-[#0198D6]" title="Download PDF"><Download className="w-4 h-4" /></button>
                      {appr === "approved" && c.customer_email && (
                        <button onClick={() => sendToCustomer(c)} data-testid={`send-cert-${c.id}`} title={`Email PDF to ${c.customer_email}`} className="text-gray-500 hover:text-[#059669]"><Send className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => downloadFromTemplate(c.id, c.certificate_number)} data-testid={`download-cert-tpl-${c.id}`} className="text-gray-500 hover:text-[#01ADEF]" title="Generate from uploaded template"><FileStack className="w-4 h-4" /></button>
                      {user?.role === 'admin' && <button data-testid={`delete-cert-${c.id}`} onClick={() => setConfirmDel(c)} className="text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>}
                    </Td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="rounded-md">
          <DialogHeader><DialogTitle className="font-display">Reject Certificate {rejectFor?.certificate_number}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Reason *</Label>
            <Textarea data-testid="reject-reason-input" autoFocus value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Describe why this certificate cannot be released to the customer" className="rounded-md mt-1 min-h-[100px]" />
            <p className="text-xs text-gray-500 mt-2">The inspector will be notified in-app with this reason.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button data-testid="confirm-reject-btn" onClick={submitReject} className="rounded-md bg-[#DC2626] hover:bg-[#B91C1C]">Reject &amp; Notify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit trail dialog */}
      <Dialog open={!!auditFor} onOpenChange={(o) => !o && setAuditFor(null)}>
        <DialogContent className="rounded-md max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Audit Trail — {auditFor?.certificate_number}</DialogTitle></DialogHeader>
          <div className="py-2 max-h-[400px] overflow-y-auto" data-testid="audit-events">
            {auditEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">No audit events yet.</div>
            ) : (
              <div className="space-y-3">
                {auditEvents.map((e) => (
                  <div key={e.id} className="border-l-2 border-gray-300 pl-4 py-1" data-testid={`audit-event-${e.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold capitalize">{e.action.replace("_", " ")}</div>
                      <div className="text-xs text-gray-500 font-mono">{new Date(e.created_at).toLocaleString()}</div>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      <span className="font-mono">{e.from_status}</span> → <span className="font-mono font-bold">{e.to_status}</span> · by <b>{e.actor_name}</b> ({e.actor_role})
                    </div>
                    {e.reason && <div className="text-xs text-[#B91C1C] mt-1 italic">&ldquo;{e.reason}&rdquo;</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
          {accessLogs.length > 0 && (
            <div className="border-t border-gray-200 pt-3 mt-1" data-testid="cert-access-log">
              <div className="text-xs uppercase tracking-wider font-semibold text-gray-600 mb-2">Customer Access</div>
              <div className="space-y-2 max-h-[160px] overflow-y-auto">
                {accessLogs.map(a => (
                  <div key={a.id} className="flex items-center justify-between text-xs" data-testid={`access-log-${a.id}`}>
                    <span className="font-mono truncate">{a.actor_email}</span>
                    <span className="text-gray-500 shrink-0 ml-2 capitalize">{a.event} · {new Date(a.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter><Button className="rounded-md" onClick={() => setAuditFor(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmTypeDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        phrase="DELETE"
        title={`Delete certificate ${confirmDel?.certificate_number || ''}?`}
        description="This permanently removes the certificate. Customers will lose access to the PDF. This cannot be undone."
        confirmLabel="Delete Certificate"
        onConfirm={async () => { await remove(confirmDel.id); }}
        testidPrefix="cert-delete"
      />

      <ConfirmTypeDialog
        open={confirmBulkDel}
        onOpenChange={setConfirmBulkDel}
        phrase="DELETE"
        title={`Delete ${selectedIds.length} certificate(s)?`}
        description="This permanently removes all selected certificates including approved ones. Customers will lose access to their PDFs. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.length}`}
        onConfirm={async () => { await bulkDelete(); }}
        testidPrefix="cert-bulk-delete"
      />
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const CertStatus = ({ s, d }) => {
  const map = { valid: ["bg-[#D1FAE5]","text-[#065F46]","border-[#6EE7B7]",`Valid · ${d}d`], expiring_soon: ["bg-[#FEF3C7]","text-[#92400E]","border-[#FCD34D]",`Expiring · ${d}d`], expired: ["bg-[#FEE2E2]","text-[#B91C1C]","border-[#FCA5A5]",`Expired ${Math.abs(d)}d`], unknown: ["bg-gray-100","text-gray-700","border-gray-300","—"] };
  const [bg,tx,br,label] = map[s] || map.unknown;
  return <span className={`inline-block text-xs font-bold px-2 py-1 uppercase tracking-wider border ${bg} ${tx} ${br}`}>{label}</span>;
};
const ApprovalBadge = ({ s }) => {
  const map = {
    pending: ["bg-[#FEF3C7]", "text-[#92400E]", "border-[#FCD34D]", "Pending"],
    approved: ["bg-[#D1FAE5]", "text-[#065F46]", "border-[#6EE7B7]", "Approved"],
    rejected: ["bg-[#FEE2E2]", "text-[#B91C1C]", "border-[#FCA5A5]", "Rejected"],
  };
  const [bg, tx, br, label] = map[s] || map.approved;
  return <span data-testid={`approval-badge-${s}`} className={`inline-block text-xs font-bold px-2 py-1 uppercase tracking-wider border ${bg} ${tx} ${br}`}>{label}</span>;
};
const ApprovalCard = ({ icon: Icon, color, label, value, active, onClick, ...rest }) => (
  <button onClick={onClick} {...rest} className={`bg-white border p-5 text-left transition-all ${active ? "border-[#01ADEF] shadow-[0_0_0_1px_#01ADEF]" : "border-gray-200 hover:border-gray-300"}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-wider font-semibold text-gray-500">{label}</span>
      <div className={`w-8 h-8 ${color} flex items-center justify-center`}><Icon className="w-4 h-4 text-white" strokeWidth={2.5} /></div>
    </div>
    <div className="font-display text-4xl font-bold tracking-tight">{value}</div>
  </button>
);
